import { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

/**
 * Inline tooltip for trigger words within body copy.
 * - Desktop: hover to show positioned tooltip.
 * - Mobile: tap to open a bottom sheet overlay.
 * - Escape or outside-click/tap closes.
 */
export const Tooltip = ({
  children,
  content,
}: {
  children: React.ReactNode;
  content: React.ReactNode;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  // Track whether the tooltip was opened via click, so hover-leave doesn't
  // accidentally close a tooltip the user clicked to pin open.
  const [openedByClick, setOpenedByClick] = useState(false);
  const wrapRef = useRef<HTMLSpanElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    setOpenedByClick(false);
  }, []);

  // Close on Escape or outside click
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    const onClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        close();
      }
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [isOpen, close]);

  // Lock body scroll on mobile when open
  useEffect(() => {
    if (!isOpen) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (!isMobile) return;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  return (
    <span ref={wrapRef} className="relative inline-block">
      <button
        type="button"
        onMouseEnter={() => {
          if (!openedByClick) setIsOpen(true);
        }}
        onMouseLeave={() => {
          // Only auto-close on mouse leave if the tooltip was opened via hover
          if (!openedByClick) setIsOpen(false);
        }}
        onFocus={() => {
          if (!openedByClick) setIsOpen(true);
        }}
        onBlur={() => {
          if (!openedByClick) setIsOpen(false);
        }}
        onClick={(e) => {
          e.stopPropagation();
          setIsOpen((v) => {
            const next = !v;
            setOpenedByClick(next);
            return next;
          });
        }}
        aria-expanded={isOpen}
        className="inline font-[inherit] text-[inherit] leading-[inherit] italic text-fwd-red border-b-2 border-dotted border-fwd-red/60 pb-0.5 cursor-help hover:border-fwd-red transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-fwd-red/50 rounded-sm"
      >
        {children}
      </button>

      {/* Desktop: positioned tooltip */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="tooltip"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2 }}
            className="hidden md:block absolute z-[9999] left-1/2 -translate-x-1/2 top-full mt-3 w-[min(22rem,85vw)] bg-fwd-ink text-white text-sm text-left rounded-lg shadow-[0_20px_50px_rgba(17,17,24,0.35)] p-5"
          >
            <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-fwd-ink rotate-45" />
            <div className="relative">{content}</div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile: bottom sheet */}
      <AnimatePresence>
        {isOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="md:hidden fixed inset-0 z-[9998] bg-black/50"
              onClick={close}
            />
            <motion.div
              role="dialog"
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 300 }}
              className="md:hidden fixed bottom-0 left-0 right-0 z-[9999] bg-fwd-ink text-white rounded-t-2xl shadow-[0_-10px_40px_rgba(17,17,24,0.4)] max-h-[75vh] overflow-y-auto"
            >
              {/* Drag handle */}
              <div className="sticky top-0 bg-fwd-ink pt-3 pb-2 flex justify-center rounded-t-2xl">
                <div className="w-10 h-1 rounded-full bg-white/20" />
              </div>
              <div className="px-6 pb-8 text-sm text-left">{content}</div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </span>
  );
};
